import { useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { useDemoState } from "@/data/demoState";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Eye, CheckCircle2, XCircle, MessageSquare, FileText,
  Send, Copy, ExternalLink, Sparkles, Info
} from "lucide-react";
import { Separator } from "@/components/ui/separator";

export default function ReviewCentre() {
  const { evidence, conditions } = useDemoState();
  const reviewableEvidence = evidence.filter((e) => e.status === "needs-review" || e.status === "approved" || e.status === "pending-match");
  const [selectedEvidence, setSelectedEvidence] = useState(reviewableEvidence[0] || null);
  const [draftOpen, setDraftOpen] = useState(false);
  const matchedCondition = selectedEvidence?.conditionId
    ? conditions.find((c) => c.id === selectedEvidence.conditionId)
    : null;

  const handleAction = (action) => {
    // Mock action
  };

  return (
    <AppLayout>
      <div className="space-y-4" data-testid="review-centre-page">
        {/* Page explainers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className="border-blue-100 bg-blue-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-blue-700 mb-0.5">What this page does</p>
                <p className="text-xs text-blue-600">Review evidence against conditions. See the source excerpt, accept, request more, or add notes.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Eye className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">What happens next</p>
                <p className="text-xs text-slate-500">Accepted evidence updates the builder's condition and readiness states. Requested-more is tracked in communication log.</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200 bg-slate-50/30">
            <CardContent className="py-3 px-4 flex items-start gap-2.5">
              <Info className="w-4 h-4 text-slate-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-slate-600 mb-0.5">Why this matters</p>
                <p className="text-xs text-slate-500">Review layer only. Certification decisions remain outside this system. This is a readiness and proof support layer.</p>
              </div>
            </CardContent>
          </Card>
        </div>
        {/* Disclaimer */}
        <Card className="border-slate-200 bg-slate-50/30">
          <CardContent className="py-2.5 px-4 flex items-center gap-2">
            <Info className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <p className="text-xs text-slate-500">
              This is a review layer only. Certification decision remains outside this system.
            </p>
          </CardContent>
        </Card>

        {/* Three panel layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4" data-testid="three-panel-layout">
          {/* Left: Evidence list */}
          <div className="lg:col-span-3">
            <Card className="border-slate-200">
              <CardHeader className="pb-2 px-3">
                <CardTitle className="text-sm font-heading">Evidence</CardTitle>
              </CardHeader>
              <CardContent className="px-2 pb-2 space-y-1">
                {reviewableEvidence.map((ev) => (
                  <div
                    key={ev.id}
                    className={`p-2.5 rounded-lg cursor-pointer transition-all ${
                      selectedEvidence?.id === ev.id
                        ? "bg-blue-50 border border-blue-200"
                        : "hover:bg-slate-50 border border-transparent"
                    }`}
                    onClick={() => { setSelectedEvidence(ev); setDraftOpen(false); }}
                    data-testid={`review-ev-${ev.id}`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <p className="text-xs font-medium text-slate-900 truncate">{ev.fileName}</p>
                    </div>
                    <p className="text-[10px] text-slate-400 truncate">{ev.uploadedBy}</p>
                    <StatusBadge status={ev.status} size="sm" className="mt-1" />
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          {/* Middle: Condition review */}
          <div className="lg:col-span-5">
            <Card className="border-slate-200 h-full">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading flex items-center gap-2">
                  <Eye className="w-4 h-4 text-blue-600" />
                  Condition Review
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedEvidence ? (
                  <>
                    {/* File preview */}
                    <div className="bg-slate-50 rounded-lg h-36 flex items-center justify-center border border-slate-200">
                      <div className="text-center">
                        <FileText className="w-8 h-8 text-slate-300 mx-auto mb-1" />
                        <p className="text-xs text-slate-400">{selectedEvidence.fileName}</p>
                        <p className="text-[10px] text-slate-300">{selectedEvidence.fileSize}</p>
                      </div>
                    </div>

                    {/* Matched condition */}
                    {matchedCondition ? (
                      <div className="bg-white border border-slate-200 rounded-lg p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-[10px] font-mono">{matchedCondition.number}</Badge>
                          <p className="text-sm font-medium text-slate-900">{matchedCondition.title}</p>
                        </div>
                        <p className="text-xs text-slate-500">{matchedCondition.plainEnglish}</p>
                        <div className="flex items-center gap-2">
                          <StatusBadge status={selectedEvidence.status} size="sm" />
                          {selectedEvidence.matchConfidence && (
                            <span className="text-[10px] text-slate-400">Match: {selectedEvidence.matchConfidence}%</span>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
                        <p className="text-xs text-amber-700 font-medium">No confirmed condition match</p>
                        <p className="text-[10px] text-amber-600">This evidence needs to be manually matched to a condition.</p>
                      </div>
                    )}

                    {/* Evidence checklist */}
                    <div className="space-y-2">
                      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Review checklist</p>
                      {[
                        { label: "Document is legible and complete", checked: true },
                        { label: "Matches the correct condition", checked: !!matchedCondition },
                        { label: "Signed and dated (if required)", checked: false },
                        { label: "Current version (not superseded)", checked: true },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            item.checked ? "bg-blue-600 border-blue-600" : "border-slate-300"
                          }`}>
                            {item.checked && <CheckCircle2 className="w-3 h-3 text-white" />}
                          </div>
                          <span className="text-xs text-slate-600">{item.label}</span>
                        </div>
                      ))}
                    </div>

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2 pt-2">
                      <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700 gap-1.5" onClick={() => handleAction("accept")} data-testid="review-accept-btn">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Accept
                      </Button>
                      <Button size="sm" variant="outline" className="text-amber-600 border-amber-200 hover:bg-amber-50 gap-1.5" onClick={() => setDraftOpen(true)} data-testid="review-request-more-btn">
                        <Send className="w-3.5 h-3.5" /> Request more
                      </Button>
                      <Button size="sm" variant="ghost" className="gap-1.5" data-testid="review-add-note-btn">
                        <MessageSquare className="w-3.5 h-3.5" /> Add note
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-slate-400">Select evidence to review</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right: Source + Audit */}
          <div className="lg:col-span-4 space-y-4">
            {/* Source excerpt */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading">Source Excerpt</CardTitle>
              </CardHeader>
              <CardContent>
                {matchedCondition ? (
                  <div className="space-y-3">
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <p className="text-xs text-slate-700 italic leading-relaxed">"{matchedCondition.description}"</p>
                      <p className="text-[10px] text-slate-400 mt-2">{matchedCondition.source}</p>
                    </div>
                    <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-3">
                      <p className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider mb-1">Plain English</p>
                      <p className="text-xs text-blue-800">{matchedCondition.plainEnglish}</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No matched condition source available.</p>
                )}
              </CardContent>
            </Card>

            {/* Audit trail */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading">Audit Trail</CardTitle>
              </CardHeader>
              <CardContent>
                {matchedCondition && matchedCondition.activity.length > 0 ? (
                  <div className="space-y-2">
                    {matchedCondition.activity.map((a, i) => (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span className="text-slate-400 shrink-0 w-20">{a.date}</span>
                        <div className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${
                          a.type === "upload" ? "bg-blue-500" : a.type === "review" ? "bg-emerald-500" : "bg-slate-400"
                        }`} />
                        <span className="text-slate-600">{a.action}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No audit trail available.</p>
                )}
              </CardContent>
            </Card>

            {/* Comments */}
            <Card className="border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-heading">Review Notes</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-400 italic">
                  No review notes yet. Click "Add note" to add one.
                </div>
              </CardContent>
            </Card>

            {/* Draft panel */}
            {draftOpen && (
              <Card className="border-blue-200 bg-blue-50/30" data-testid="draft-clarification-panel">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-heading flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                    AI Draft: Clarification Request
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="bg-white border border-slate-200 rounded-lg p-3">
                    <pre className="text-xs text-slate-700 whitespace-pre-wrap font-body leading-relaxed">
{`Dear ${selectedEvidence?.uploadedBy || "Consultant"},

Re: ${selectedEvidence?.project} - ${matchedCondition?.title || "Evidence Review"}

We have reviewed the submitted evidence and require additional information or clarification:

1. [Specify what additional information is needed]
2. [Specify any quality or completeness concerns]

Please upload the updated documentation using the secure link provided.

Kind regards,
Mark Stevens
Surveyor`}
                    </pre>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs" data-testid="copy-clarification-btn">
                      <Copy className="w-3 h-3" /> Copy
                    </Button>
                    <Button size="sm" variant="outline" className="gap-1.5 text-xs">
                      <ExternalLink className="w-3 h-3" /> Open in email
                    </Button>
                    <Button size="sm" variant="ghost" className="text-xs" onClick={() => setDraftOpen(false)}>
                      Close
                    </Button>
                  </div>
                  <p className="text-[10px] text-blue-600 italic">
                    AI-drafted for convenience. Review and send from your own email client. Never auto-sent.
                  </p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
